import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMatchRoute } from '@tanstack/react-router';
import { listConversations, type Conversation } from '../api/conversations';
import { getApiClient } from '../api/factory';
import { useT } from '../i18n';
import { isDesktop } from '../lib/desktop-bridge';
import { playBeep, showBrowserNotification } from '../lib/notify';

// ANX-053 — нотификации (звук + browser-notification) на сообщения visitor'ов.
//
// Триггер — рост `lastMessageAt` в кэше списка диалогов. Тот же query, что
// у ConversationsRail (`['conversations', 'open']`) — TanStack Query шарит
// кэш по queryKey, дублирующего HTTP-запроса не делается.
//
// Гашение: если оператор в данный момент focused на панели И открыт именно
// тот диалог, который обновился — не дёргаем. Если фокус на другой вкладке
// (Slack/email) ИЛИ оператор на другой странице панели (sites/settings) —
// уведомляем.

export function useMessageNotifier(): void {
  const t = useT();
  const matchRoute = useMatchRoute();

  // Подписываемся на тот же query, что rail. TanStack дедуплицирует.
  const { data } = useQuery({
    queryKey: ['conversations', 'open'],
    queryFn: () => listConversations(getApiClient(), { status: 'open' }),
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });

  // Запомненный max(lastMessageAt) — чтобы при первом рендере не fire'ить
  // на стартовом снимке. initialized=false блокирует первый callback.
  const seenRef = useRef<{ initialized: boolean; max: number }>({
    initialized: false,
    max: 0,
  });

  useEffect(() => {
    if (!data) return;
    const items: Conversation[] = data.conversations;
    let maxTs = 0;
    let newest: Conversation | undefined;
    for (const c of items) {
      const ts = Date.parse(c.lastMessageAt);
      if (!Number.isFinite(ts)) continue;
      if (ts > maxTs) {
        maxTs = ts;
        newest = c;
      }
    }

    if (!seenRef.current.initialized) {
      seenRef.current = { initialized: true, max: maxTs };
      return;
    }
    if (maxTs <= seenRef.current.max || !newest) return;
    seenRef.current.max = maxTs;

    // Гашение: focus + в этой conversation → молчим.
    const visible = document.visibilityState === 'visible';
    const focused = document.hasFocus();
    const inThisConv = !!matchRoute({ to: '/conversations/$id', params: { id: newest.id } });
    if (visible && focused && inThisConv) return;

    const label =
      newest.visitor?.name?.trim() ||
      newest.visitor?.email?.trim() ||
      t('conversations.visitor.fallback');
    if (isDesktop()) {
      // ANX-059-followup — Rust-поллер сам шлёт native notification +
      // звук через UserNotifications framework, даже когда webview
      // hidden. Дублировать через web Notification + WebAudio не нужно,
      // иначе при активном окне будет двойной звук.
      return;
    }
    playBeep();
    showBrowserNotification(t('notify.newMessage'), label);
  }, [data, matchRoute, t]);
}
